import {
  redirectIfWritebackField,
  redirectToApprovalsIfInvalidApplicationId,
  redirectToApprovalsIfNoExportAccess,
  redirectToApprovalsIfNoSettingsAccess,
  redirectToApprovalsIfNotDebugAccess,
  redirectToApprovalsIndexIfNotValidId,
  redirectToHomeIfNoApprovalsAccess,
} from '@/approvals/router/guards';
import { NO_AUTH_NEEDED } from '@/types';

const ApprovalsPage = () => import('@/approvals/pages/approvals.vue');
const AtsSettingsPage = () => import('@/approvals/pages/ats-settings.vue');
const ApprovalATSDebugPage = () => import('@/approvals/pages/gh-debug-tools/approval-sync.vue');
const ApprovalsSettingsPage = () => import('@/approvals/pages/approvals-settings.vue');
const ApprovalChainsPage = () => import('@/approvals/pages/approval-chains.vue');
const CompTablesPage = () => import('@/approvals/pages/comp-tables.vue');
const CompTableNewPage = () => import('@/approvals/pages/comp-table-new.vue');
const CompTableEditPage = () => import('@/approvals/pages/comp-table-edit.vue');
const ApprovalChainNewPage = () => import('@/approvals/pages/approval-chain-new.vue');
const NewApprovalRequestPage = () => import('@/approvals/pages/new-approval-request.vue');
const ApprovalChainEditPage = () => import('@/approvals/pages/approval-chain-edit.vue');
const ApprovalRequestPage = () => import('@/approvals/pages/approval-request.vue');
const EditApprovalRequestPage = () => import('@/approvals/pages/edit-approval-request.vue');
const NoAccessPage = () => import('@/approvals/pages/no-access.vue');
const CustomFieldsPage = () => import('@/approvals/pages/custom-fields.vue');
const FieldsGroupsPage = () => import('@/approvals/pages/fields-groups.vue');
const FieldsGroupsNewPage = () => import('@/approvals/pages/fields-group-new.vue');
const FieldsGroupsEditPage = () => import('@/approvals/pages/fields-group-edit.vue');
const CustomFieldNewPage = () => import('@/approvals/pages/custom-field-new.vue');
const CustomFieldEditPage = () => import('@/approvals/pages/custom-field-edit.vue');
const CompetingOffersPage = () => import('@/approvals/pages/competing-offers.vue');
const NewApprovalRequestGHPage = () => import('@/approvals/pages/gh-approval-request.vue');
const ApprovalsExportPage = () => import('@/approvals/pages/export-page.vue');
const EmailApprovalConfirmationPage = () => import('@/approvals/pages/email-approval-confirmation.vue');
const ApprovalsSettingsLayout = () => import('@/approvals/layouts/layout-approvals-settings-default.vue');

export const approvalsRoutes = [
  // Email approval confirmation - No authentication required (must be outside auth-protected routes)
  {
    path: '/approvals/email-confirmation',
    name: 'email-approval-confirmation',
    component: EmailApprovalConfirmationPage,
    meta: {
      layout: 'layout-empty', // Use minimal layout without navigation
      [NO_AUTH_NEEDED]: true, // No authentication required for email links
    },
  },
  {
    path: '/approvals/no-access',
    name: 'no-access',
    component: NoAccessPage,
  },
  {
    path: '/approvals',
    beforeEnter: redirectToHomeIfNoApprovalsAccess,
    children: [
      {
        path: '',
        name: 'approvals',
        component: ApprovalsPage,
      },
      {
        path: 'new',
        name: 'new-approval-request',
        component: NewApprovalRequestPage,
      },
      {
        path: 'new-gh/:applicationId',
        name: 'new-approval-request-gh-chrome-extension',
        component: NewApprovalRequestGHPage,
        beforeEnter: redirectToApprovalsIfInvalidApplicationId,
      },
      {
        path: 'competing-offers',
        name: 'approvals-competing-offers',
        component: CompetingOffersPage,
      },
      {
        path: ':id',
        beforeEnter: redirectToApprovalsIndexIfNotValidId,
        children: [
          {
            path: '',
            name: 'approval-request',
            component: ApprovalRequestPage,
          },
          {
            path: 'edit',
            name: 'edit-approval-request',
            component: EditApprovalRequestPage,
          },
        ],
      },
    ],
  },
  {
    path: '/approvals/settings',
    component: ApprovalsSettingsLayout,
    beforeEnter: [redirectToApprovalsIfNoSettingsAccess],
    children: [
      {
        path: '',
        name: 'approvals-settings',
        redirect: { name: 'comp-tables' },
      },
      {
        path: 'general',
        name: 'approvals-settings-general',
        component: ApprovalsSettingsPage,
      },
      {
        path: 'chains',
        name: 'approval-chains',
        component: ApprovalChainsPage,
      },
      {
        path: 'chains/new',
        name: 'approval-chains-new',
        component: ApprovalChainNewPage,
      },
      {
        path: 'chains/:id/edit',
        name: 'approval-chains-edit',
        component: ApprovalChainEditPage,
      },
      {
        path: 'ats',
        name: 'ats-settings',
        component: AtsSettingsPage,
      },
      {
        path: 'export',
        name: 'approvals-export',
        component: ApprovalsExportPage,
        beforeEnter: redirectToApprovalsIfNoExportAccess,
      },
      {
        path: 'comp-tables',
        name: 'comp-tables',
        component: CompTablesPage,
      },
      {
        path: 'comp-tables/new',
        name: 'comp-tables-new',
        component: CompTableNewPage,
      },
      {
        path: 'comp-tables/:id/edit',
        name: 'comp-tables-edit',
        component: CompTableEditPage,
      },
      {
        path: 'fields',
        name: 'custom-fields',
        component: CustomFieldsPage,
      },
      {
        path: 'fields/new',
        name: 'fields-new',
        component: CustomFieldNewPage,
      },
      {
        path: 'fields/:id/edit',
        name: 'fields-edit',
        component: CustomFieldEditPage,
        beforeEnter: redirectIfWritebackField,
      },
      {
        path: 'fields-groups',
        name: 'fields-groups',
        component: FieldsGroupsPage,
      },
      {
        path: 'fields-groups/new',
        name: 'fields-groups-new',
        component: FieldsGroupsNewPage,
      },
      {
        path: 'fields-groups/:id/edit',
        name: 'fields-groups-edit',
        component: FieldsGroupsEditPage,
      },
      {
        path: '/approvals/ats-debug',
        name: 'approvals-ats-debug',
        component: ApprovalATSDebugPage,
        beforeEnter: redirectToApprovalsIfNotDebugAccess,
      },
    ],
  },
];
